import type { AttachmentDisplay } from '@liniya/api-client';
import { attachmentKind } from '@liniya/api-client';
import { FileText, Music, Paperclip } from 'lucide-react';
import { useEffect, useState } from 'react';

import { api } from '@/api/client';
import { useUiStore } from '@liniya/client-core';

interface Props {
  attachmentId: string;
  fileName: string;
  /** A local object URL shown while the file is still uploading. */
  localPreviewUrl?: string | undefined;
}

/**
 * One attachment inside a message bubble.
 *
 * Media is loaded through a short-lived signed URL rather than an in-memory
 * blob, so images can be cached by the browser and video can be seeked without
 * downloading the whole file first. The URL is fetched only when the component
 * actually mounts, which keeps a long history from minting hundreds of tickets
 * nobody looks at.
 */
export function AttachmentView({ attachmentId, fileName, localPreviewUrl }: Props) {
  const kind = attachmentKind(fileName);
  const [display, setDisplay] = useState<AttachmentDisplay | null>(
    localPreviewUrl ? { url: localPreviewUrl, thumbnailUrl: null, width: null, height: null } : null,
  );
  const [failed, setFailed] = useState(false);
  const openViewer = useUiStore((state) => state.openMediaViewer);
  const url = display?.url ?? null;

  useEffect(() => {
    if (localPreviewUrl || attachmentId === 'pending') return;
    let cancelled = false;
    api.media
      .display(attachmentId, fileName)
      .then((resolved) => {
        if (!cancelled) setDisplay(resolved);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [attachmentId, fileName, localPreviewUrl]);

  if (failed) {
    return (
      <div className="attachment-file">
        <span className="attachment-file-icon">
          <Paperclip size={18} />
        </span>
        <span className="muted" style={{ fontSize: 12 }}>
          Вложение недоступно
        </span>
      </div>
    );
  }

  if (kind === 'image') {
    if (!url || !display) {
      return <div className="skeleton attachment-photo" style={{ height: 220 }} aria-hidden="true" />;
    }
    return (
      <img
        className="attachment-photo"
        // The bubble shows the downscaled copy; the viewer opens the original.
        // Falls back to the original when there is no thumbnail, which is the
        // case for anything uploaded before this existed.
        src={display.thumbnailUrl ?? url}
        alt={fileName}
        // Telling the browser the real proportions lets it keep the right
        // amount of room from the start, instead of every picture shoving the
        // conversation down as it arrives.
        {...(display.width && display.height
          ? { width: display.width, height: display.height }
          : {})}
        loading="lazy"
        decoding="async"
        // A ticket can expire between being minted and the element getting
        // round to loading it. Without this the bubble keeps a broken image
        // icon; with it the same "unavailable" state as any other failure.
        onError={() => setFailed(true)}
        onClick={() =>
          openViewer({
            url,
            name: fileName,
            kind: 'image',
            thumbnailUrl: display?.thumbnailUrl ?? null,
          })
        }
        style={{ cursor: 'zoom-in' }}
      />
    );
  }

  if (kind === 'video') {
    if (!url) {
      return <div className="skeleton attachment-video" style={{ height: 220 }} aria-hidden="true" />;
    }
    return (
      <video className="attachment-video" src={url} controls preload="metadata" playsInline>
        <track kind="captions" />
      </video>
    );
  }

  if (kind === 'audio') {
    return (
      <div className="attachment-file">
        <span className="attachment-file-icon">
          <Music size={18} />
        </span>
        {url ? (
          <audio src={url} controls preload="metadata" style={{ maxWidth: 240 }} />
        ) : (
          <span className="muted">Загрузка…</span>
        )}
      </div>
    );
  }

  return (
    <div className="attachment-file">
      <span className="attachment-file-icon">
        <FileText size={18} />
      </span>
      {url ? (
        <a href={url} download={fileName}>
          {fileName}
        </a>
      ) : (
        <span className="muted">{fileName}</span>
      )}
    </div>
  );
}
